import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose } from 'lodash';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage, injectIntl } from 'react-intl';
import Table from 'react-bootstrap/Table';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';

import CustomPagination from '../../CustomPagination';
import CurrencyConverter from '../../CurrencyConverter';
import Loader from '../../../components/Common/Loader';

import messages from '../../../locale/messages';
import getDirectBookings from './getDirectBookings.graphql';
import getAssignableProviders from './getAssignableProviders.graphql';
import assignBookingMutation from './assignBooking.graphql';
import refundBookingMutation from './refundBooking.graphql';
import { capitalizeFirstLetter } from '../../../helpers/capitalizeFirst';
import { showToaster } from '../../../helpers/toastrMessgaes/showToaster';
import debounce from '../../../helpers/debounce';

import s from './DirectBookingList.css';
import bt from '../../../components/commonStyle.css';

export class DirectBookingList extends Component {
	static propTypes = {
		bookingDetails: PropTypes.object,
		providersData: PropTypes.object,
	}
	constructor(props) {
		super(props)
		this.state = {
			currentPage: 1,
			selectedProvider: {}, // bookingId -> partnerId
			assigningId: null,
			refundingId: null,
		}
		this.paginationData = this.paginationData.bind(this);
		this.handleAssign = this.handleAssign.bind(this);
		this.handleRefund = this.handleRefund.bind(this);
		this.handleKeywordSearch = debounce(this.handleKeywordSearch.bind(this), 250);
	}
	componentDidMount() {
		// Client-side polling only. Setting pollInterval in the query options
		// crashes SSR (polling + forced cache-first is illegal in Apollo).
		if (this.props.bookingDetails && this.props.bookingDetails.startPolling) {
			this.props.bookingDetails.startPolling(15000);
		}
	}
	componentWillUnmount() {
		if (this.props.bookingDetails && this.props.bookingDetails.stopPolling) {
			this.props.bookingDetails.stopPolling();
		}
	}
	handleKeywordSearch(searchList) {
		const { bookingDetails: { refetch } } = this.props
		let variables = { currentPage: 1, searchList }
		this.setState({ currentPage: 1 })
		refetch(variables)
	}
	paginationData(currentPage) {
		const { bookingDetails: { refetch } } = this.props;
		this.setState({ currentPage });
		refetch({ currentPage });
	}
	async handleAssign(bookingId) {
		const { assignBookingMutation, bookingDetails: { refetch } } = this.props;
		const partnerId = this.state.selectedProvider[bookingId];
		if (!partnerId) {
			showToaster({ messageId: 'selectProvider', toasterType: 'error' });
			return;
		}
		this.setState({ assigningId: bookingId });
		try {
			const { data } = await assignBookingMutation({ variables: { bookingId, partnerId } });
			if (data?.assignBooking?.status === 200) {
				showToaster({ messageId: 'assignSuccess', toasterType: 'success' });
				refetch();
			} else {
				showToaster({ messageId: 'errorMessage', requestContent: { content: data?.assignBooking?.errorMessage }, toasterType: 'error' });
			}
		} catch (error) {
			showToaster({ messageId: 'catchMessage', requestContent: { content: error }, toasterType: 'error' });
		}
		this.setState({ assigningId: null });
	}
	async handleRefund(bookingId) {
		/*
		 * Nominal dan alasan sekarang ditanyakan. Refund tidak lagi terbatas pada
		 * direct booking yang belum ditugaskan, jadi sebagiannya menyangkut
		 * pekerjaan yang sudah terjadi — dan di situ jumlah yang benar hanya
		 * diketahui manusia, bukan kode.
		 */
		const rawAmount = window.prompt(
			'Refund amount.\n\nLeave empty for a FULL refund, or enter a smaller number for a partial refund.'
		);
		if (rawAmount === null) return;

		const amount = String(rawAmount).trim() === '' ? null : Number(rawAmount);
		if (amount !== null && (!Number.isFinite(amount) || amount <= 0)) {
			showToaster({ messageId: 'errorMessage', requestContent: { content: 'Enter a valid amount, or leave it empty for a full refund.' }, toasterType: 'error' });
			return;
		}

		const reason = window.prompt('Reason for this refund (required):');
		if (reason === null) return;
		if (String(reason).trim().length < 3) {
			showToaster({ messageId: 'errorMessage', requestContent: { content: 'A reason is required.' }, toasterType: 'error' });
			return;
		}

		const { refundBookingMutation, bookingDetails: { refetch } } = this.props;
		this.setState({ refundingId: bookingId });
		try {
			const { data } = await refundBookingMutation({ variables: { bookingId, amount, reason } });
			if (data?.refundBooking?.status === 200) {
				showToaster({ messageId: 'refundSuccess', toasterType: 'success' });
				refetch();
			} else {
				showToaster({ messageId: 'errorMessage', requestContent: { content: data?.refundBooking?.errorMessage }, toasterType: 'error' });
			}
		} catch (error) {
			showToaster({ messageId: 'catchMessage', requestContent: { content: error }, toasterType: 'error' });
		}
		this.setState({ refundingId: null });
	}
	render() {
		const { bookingDetails, providersData, loading } = this.props;
		const { currentPage, selectedProvider, assigningId, refundingId } = this.state;
		const { formatMessage } = this.props.intl;
		const providers = (providersData && providersData.getAssignableProviders && providersData.getAssignableProviders.result) || [];
		const providerName = (id) => {
			const p = providers.find((x) => String(x.id) === String(id));
			return (p && (p.name || p.id)) || id;
		};

		/*
		 * Saldo ikut ditampilkan di dropdown karena penugasan manual melewati
		 * saringan saldo yang berlaku pada dispatch otomatis. Provider bersaldo
		 * minus sedang berutang komisi job cash dan tidak lagi menerima job cash
		 * secara otomatis — admin boleh menimpanya, tapi harus melakukannya
		 * dengan sadar, bukan karena tidak tahu.
		 */
		const providerLabel = (p) => {
			const parts = [p.name || p.id];
			if (!p.isActive) parts.push('(offline)');
			if (typeof p.walletBalance === 'number') {
				const currency = p.currency || '';
				const sign = p.walletBalance < 0 ? '⚠ ' : '';
				parts.push(`— ${sign}${currency} ${p.walletBalance.toFixed(2)}`);
			}
			return parts.join(' ');
		};

		return (
			<div className={s.widthInner}>
				<div className={cx(s.searchInput, 'searchInputRTL')}>
					<FormControl type='text' placeholder={formatMessage(messages.searchOnly)} onChange={(e) => this.handleKeywordSearch(e.target && e.target.value)} className={bt.formControlInput} />
				</div>

				{loading && <div><Loader type="circle" /></div>}
				{!loading &&
					<div className={cx(s.tableCss, 'tableCss', 'tableSticky', 'NewResponsiveTable')}>
						<Table className="table">
							<thead>
								<tr>
									<th scope="col"><FormattedMessage {...messages.id} /></th>
									<th scope="col"><FormattedMessage {...messages.userName} /></th>
									<th scope="col"><FormattedMessage {...messages.category} /></th>
									<th scope="col"><FormattedMessage {...messages.totalFare} /></th>
									<th scope="col">Status</th>
									<th scope="col"><FormattedMessage {...messages.assignProvider} /></th>
									<th scope="col"><FormattedMessage {...messages.action} /></th>
								</tr>
							</thead>
							<tbody>
								{
									bookingDetails?.getDirectBookings?.results?.map((item, index) => (
										<tr key={index}>
											<td data-label={formatMessage(messages.id)}>{item.id}</td>
											<td data-label={formatMessage(messages.userName)}>{item && item.userDetails && capitalizeFirstLetter(item.userDetails.firstName)}</td>
											<td data-label={formatMessage(messages.category)}>{item && item.categoryDetails && item.categoryDetails.name}</td>
											<td data-label={formatMessage(messages.totalFare)}><CurrencyConverter from={item.currency} amount={item.userPayableFare} /></td>
											<td data-label="Status">
												{item.partnerId
													? <span style={{ color: '#c47f00' }}>Waiting acceptance — {providerName(item.partnerId)}</span>
													: <span style={{ color: '#0a7d32' }}>Unassigned</span>}
											</td>
											<td data-label={formatMessage(messages.assignProvider)}>
												{item.partnerId
													? <span>—</span>
													: <React.Fragment>
														<FormControl
															as="select"
															className={cx(s.assignSelect, bt.formControlSelect)}
															value={selectedProvider[item.id] || ''}
															onChange={(e) => this.setState({ selectedProvider: { ...selectedProvider, [item.id]: e.target.value } })}
														>
															<option value="">{formatMessage(messages.selectProvider)}</option>
															{providers.map((p) => (
																<option key={p.id} value={p.id}>
																	{providerLabel(p)}
																</option>
															))}
														</FormControl>
														<Button onClick={() => this.handleAssign(parseInt(item.id, 10))} disabled={assigningId === item.id}>
															<FormattedMessage {...messages.assignProvider} />
														</Button>
													</React.Fragment>}
											</td>
											<td data-label={formatMessage(messages.action)}>
												{item.partnerId
													? <span>—</span>
													: <Button
														variant="danger"
														onClick={() => this.handleRefund(parseInt(item.id, 10))}
														disabled={refundingId === item.id}
													>
														Cancel & Refund
													</Button>}
											</td>
										</tr>
									))
								}
								{
									!loading && bookingDetails?.getDirectBookings?.results?.length == 0 && (
										<tr><td colspan="7" className={s.noRecords}><FormattedMessage {...messages.noResult} /></td></tr>
									)
								}
							</tbody>
						</Table>
					</div>
				}
				{
					bookingDetails?.getDirectBookings?.results?.length > 0
					&& <div className={cx(bt.space5, bt.spaceTop5)}>
						<CustomPagination
							total={bookingDetails.getDirectBookings.count}
							currentPage={currentPage}
							defaultCurrent={1}
							defaultPageSize={10}
							change={this.paginationData}
							paginationLabel={formatMessage(messages.bookings)}
						/>
					</div>
				}
			</div>
		)
	}
}
const mapState = (state) => ({
	loading: state.intl.loading
});
export default compose(
	injectIntl,
	withStyles(s, bt),
	connect(mapState),
	graphql(getDirectBookings, {
		name: 'bookingDetails',
		options: { variables: { currentPage: 1, searchList: '' }, fetchPolicy: 'network-only' },
	}),
	graphql(getAssignableProviders, {
		name: 'providersData',
		options: { fetchPolicy: 'network-only' },
	}),
	graphql(assignBookingMutation, { name: 'assignBookingMutation' }),
	graphql(refundBookingMutation, { name: 'refundBookingMutation' }),
)(DirectBookingList);
